/**
 * Market / position pair helpers shared by Oroboros terminal surfaces.
 * Kept free of fetch/client imports so unit tests can load without Vite env.
 */

/**
 * Base coin for matching open positions to the selected market.
 * Strips venue/dex prefixes and quote suffixes (USD/USDC/…).
 * Mirrors `hlPositionCoinKey` so BTC-PERP matches BTC-USD and xyz:SILVER matches SILVER-USDC.
 */
export function oroPositionCoinKey(pair: string): string {
  let s = String(pair || '').trim().toUpperCase();
  if (!s) return '';
  if (s.startsWith('HL:')) s = s.slice(3);
  const venueId = s.match(/^HYPERLIQUID:[^:]+:(.+)$/i);
  if (venueId) s = venueId[1];
  if (s.includes(':')) s = s.split(':').pop() || s;
  s = s.replace(/\//g, '-').replace(/-PERP$/i, '');
  return s
    .replace(/-(USDT0|USDH|USDE|USDC|USDT|USD)$/i, '')
    .replace(/(USDT0|USDH|USDE|USDC|USDT|USD)$/i, '');
}

export function pairToPerpLabel(pair: string): string {
  const raw = String(pair || '').trim();
  if (!raw) return 'BTC-PERP';
  if (raw.includes(':')) {
    return raw.replace(/-PERP$/i, '').replace(/-(USDT0|USDH|USDE|USDC|USDT|USD)$/i, '');
  }
  // HIP-3 style without dex prefix — keep BASE-QUOTE label.
  if (/-(USDT0|USDH|USDE|USDC|USDT)$/i.test(raw)) {
    return raw.replace(/-PERP$/i, '').toUpperCase();
  }
  const coin = oroPositionCoinKey(raw);
  if (!coin) return 'BTC-PERP';
  if (coin.includes('-PERP')) return coin;
  return `${coin}-PERP`;
}

export function pairToHlCoin(pair: string): string {
  const raw = String(pair || '').trim();
  if (!raw) return '';
  // HIP-3 / dex markets keep `dex:COIN` for HL info requests.
  if (raw.includes(':')) {
    return raw
      .replace(/-PERP$/i, '')
      .replace(/-(USDT0|USDH|USDE|USDC|USDT|USD)$/i, '');
  }
  // HIP-3 style without dex prefix — keep BASE-QUOTE for feed/execute.
  if (/-(USDT0|USDH|USDE|USDC|USDT)$/i.test(raw)) {
    return raw.replace(/-PERP$/i, '').toUpperCase();
  }
  return oroPositionCoinKey(raw) || raw.replace(/-PERP$/i, '');
}

export type OroMarketRef = {
  id: string;
  coin?: string;
  kind?: string;
};

function isHip3MarketRef(row: OroMarketRef): boolean {
  const kind = String(row.kind || '').toLowerCase();
  if (kind === 'hip3') return true;
  if (kind === 'perp') return false;
  return String(row.id || '').includes(':');
}

/**
 * Map a position pair or typed symbol onto an id that exists on the HL tape.
 * Propr quotes core perps as BASE-USDC (BTC-USDC) — that is not HIP-3 xyz:BTC.
 */
export function resolveOroMarketId(raw: string, markets: OroMarketRef[]): string {
  const requested = String(raw || '').trim();
  if (!requested) return 'BTC-PERP';

  const findById = (id: string) =>
    markets.find((m) => m.id.toUpperCase() === id.toUpperCase());

  const exact = findById(requested);
  if (exact) return exact.id;

  const coin = oroPositionCoinKey(requested);
  if (!coin) return pairToPerpLabel(requested);

  const looksHip3Quoted =
    requested.includes(':') || /-(USDT0|USDH|USDE|USDC|USDT)$/i.test(requested);

  const core = markets.find((m) => {
    if (isHip3MarketRef(m)) return false;
    return oroPositionCoinKey(m.coin || m.id) === coin;
  });
  const hip3 = markets.find((m) => {
    if (!isHip3MarketRef(m)) return false;
    return oroPositionCoinKey(m.id) === coin || oroPositionCoinKey(m.coin || '') === coin;
  });

  if (looksHip3Quoted) {
    return hip3?.id || core?.id || pairToPerpLabel(requested);
  }
  return core?.id || hip3?.id || pairToPerpLabel(requested);
}
